import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Chip, IconButton, TextField } from '@lumx/react';
import MdiIcon from '@mdi/react';
import { mdiAccountMultipleOutline, mdiDotsVertical, mdiMagnify, mdiSitemap } from '@mdi/js';
import { NodeType, OUNode, Site } from '../types';
import { formatDate } from '../utils/treeUtils';
import { Icon } from './shared/icons';
import OUPickerDropdown from './shared/OUPickerDropdown';

type FilterTab = 'all' | 'assigned' | 'unassigned';

interface SiteRowProps {
  site: Site;
  nodes: OUNode[];
  allSites: Site[];
  isDragging: boolean;
  isDragOver: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onAssign: (siteId: string, nodeId: string) => void;
  onUnassign: (siteId: string) => void;
  onCreate?: (name: string, type: NodeType, parentId: string | null) => void;
  onViewSiteGroups: (siteId: string, siteName: string) => void;
}

const AssignPicker: React.FC<{
  site: Site;
  nodes: OUNode[];
  allSites: Site[];
  assignedNode: OUNode | null | undefined;
  isGlobal: boolean;
  onAssign: (siteId: string, nodeId: string) => void;
  onUnassign: (siteId: string) => void;
  onCreate?: (name: string, type: NodeType, parentId: string | null) => void;
}> = ({ site, nodes, allSites, assignedNode, isGlobal, onAssign, onUnassign, onCreate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isAssigned = site.assignedNodeId !== null;

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const triggerIcon = isGlobal ? Icon.globe
    : assignedNode ? (assignedNode.type === 'Location' ? Icon.location : Icon.building)
    : null;
  const triggerText = isGlobal ? 'Global / Platform'
    : assignedNode ? assignedNode.name
    : '+ Assign to unit';

  return (
    <div className="picker-wrap" ref={ref} style={{ maxWidth: 200 }}>
      <button
        className={`picker-trigger ${isAssigned ? 'assigned' : 'empty'}`}
        onClick={() => setOpen(v => !v)}
      >
        {triggerIcon && <span className="picker-trigger-icon">{triggerIcon}</span>}
        <span className="picker-trigger-name">{triggerText}</span>
        {Icon.chevronDown}
      </button>
      {open && (
        <OUPickerDropdown
          nodes={nodes}
          sites={allSites}
          onSelect={nodeId => { onAssign(site.id, nodeId); setOpen(false); }}
          onCreate={onCreate ? (name, type, parentId) => { onCreate(name, type, parentId); setOpen(false); } : undefined}
          onUnassign={isAssigned ? () => { onUnassign(site.id); setOpen(false); } : undefined}
        />
      )}
    </div>
  );
};

const SiteOverflowMenu: React.FC<{
  isAssigned: boolean;
  onUnassign: () => void;
}> = ({ isAssigned, onUnassign }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isAssigned) return null;

  return (
    <div className="overflow-menu-wrap" ref={ref}>
      <button
        className={`overflow-menu-btn ${open ? 'is-open' : ''}`}
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
      >
        <MdiIcon path={mdiDotsVertical} size={0.7} />
      </button>
      {open && (
        <div className="overflow-menu">
          <button className="overflow-menu-item" onClick={() => { onUnassign(); setOpen(false); }}>
            Unassign
          </button>
        </div>
      )}
    </div>
  );
};

const SiteRow: React.FC<SiteRowProps> = ({
  site, nodes, allSites, isDragging, isDragOver,
  isSelected, onToggle, onDragStart, onAssign, onUnassign, onCreate, onViewSiteGroups,
}) => {
  const assignedNode = site.assignedNodeId
    ? nodes.find(n => n.id === site.assignedNodeId)
    : null;
  const isAssigned = site.assignedNodeId !== null;

  return (
    <div
      className={`site-list-row ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
      draggable={!isAssigned}
      onDragStart={!isAssigned ? onDragStart : undefined}
    >
      <div className="row-controls">
        {!isAssigned && (
          <span className="drag-handle" title="Drag to assign">{Icon.drag}</span>
        )}
      </div>

      <div className="site-row-checkbox">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
        />
      </div>

      <div className="site-row-name">
        <span className="name-icon">{Icon.site}</span>
        <span className="name-text">{site.name}</span>
      </div>

      <div className="site-row-ou">
        <AssignPicker
          site={site}
          nodes={nodes}
          allSites={allSites}
          assignedNode={assignedNode}
          isGlobal={!assignedNode && site.assignedNodeId === '__global__'}
          onAssign={onAssign}
          onUnassign={onUnassign}
          onCreate={onCreate}
        />
      </div>

      <div className="site-row-status">
        <Chip color={isAssigned ? 'green' : 'grey'} size="s">
          {isAssigned ? 'Mapped' : 'Not mapped'}
        </Chip>
      </div>


      <div className="site-row-actions" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <IconButton
          label="View site groups"
          icon={mdiAccountMultipleOutline}
          emphasis="low"
          size="s"
          onClick={e => { (e as React.MouseEvent).stopPropagation(); onViewSiteGroups(site.id, site.name); }}
        />
        <SiteOverflowMenu
          isAssigned={isAssigned}
          onUnassign={() => onUnassign(site.id)}
        />
      </div>
    </div>
  );
};

interface Props {
  sites: Site[];
  nodes: OUNode[];
  draggingId: string | null;
  onDragStart: (e: React.DragEvent, kind: 'site', id: string) => void;
  onAssign: (siteId: string, nodeId: string) => void;
  onUnassign: (siteId: string) => void;
  onCreateAndAssign?: (siteId: string, name: string, type: NodeType, parentId: string | null) => void;
  onBulkAssign: (siteIds: string[], nodeId: string) => void;
  onBulkCreateAndAssign?: (siteIds: string[], name: string, type: NodeType, parentId: string | null) => void;
  onViewSiteGroups: (siteId: string, siteName: string) => void;
}

const SitesPane: React.FC<Props> = ({
  sites, nodes, draggingId,
  onDragStart, onAssign, onUnassign, onCreateAndAssign,
  onBulkAssign, onBulkCreateAndAssign, onViewSiteGroups,
}) => {
  const [filter, setFilter] = useState<FilterTab>('unassigned');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);
  const [bulkPickerPos, setBulkPickerPos] = useState<{ bottom: number; left: number } | null>(null);
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const bulkBarRef = useRef<HTMLDivElement>(null);
  const bulkMapRef = useRef<HTMLDivElement>(null);
  const bulkMapBtnRef = useRef<HTMLButtonElement>(null);
  const bulkPickerPortalRef = useRef<HTMLDivElement>(null);

  const unassignedCount = useMemo(() => sites.filter(s => s.assignedNodeId === null).length, [sites]);

  const filteredSites = useMemo(() => {
    let base = sites;
    if (filter === 'assigned') base = sites.filter(s => s.assignedNodeId !== null);
    if (filter === 'unassigned') base = sites.filter(s => s.assignedNodeId === null);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      base = base.filter(s => s.name.toLowerCase().includes(q));
    }
    if (filter === 'all') {
      return [...base].sort((a, b) => {
        const aUnassigned = a.assignedNodeId === null ? 0 : 1;
        const bUnassigned = b.assignedNodeId === null ? 0 : 1;
        if (aUnassigned !== bUnassigned) return aUnassigned - bUnassigned;
        return a.name.localeCompare(b.name);
      });
    }
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [sites, filter, searchQuery]);

  const allFilteredSelected = filteredSites.length > 0 && filteredSites.every(s => selectedIds.has(s.id));
  const someFilteredSelected = filteredSites.some(s => selectedIds.has(s.id));

  useEffect(() => {
    if (!headerCheckRef.current) return;
    headerCheckRef.current.checked = allFilteredSelected;
    headerCheckRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
  }, [allFilteredSelected, someFilteredSelected]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inBar = bulkMapRef.current?.contains(target);
      const inPortal = bulkPickerPortalRef.current?.contains(target);
      if (!inBar && !inPortal) setBulkPickerOpen(false);
    };
    if (bulkPickerOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [bulkPickerOpen]);

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredSites.forEach(s => next.delete(s.id));
        return next;
      });
    } else {
      setSelectedIds(prev => new Set([...prev, ...filteredSites.map(s => s.id)]));
    }
  };

  const handleBulkAssign = (nodeId: string) => {
    onBulkAssign(Array.from(selectedIds), nodeId);
    setSelectedIds(new Set());
    setBulkPickerOpen(false);
  };

  const handleBulkCreate = (name: string, type: NodeType, parentId: string | null) => {
    onBulkCreateAndAssign?.(Array.from(selectedIds), name, type, parentId);
    setSelectedIds(new Set());
    setBulkPickerOpen(false);
  };

  return (
    <div ref={paneRef} className="pane pane--sites" style={{ position: 'relative' }}>
      {/* Pane header */}
      <div className="pane-header">
        <div className="pane-title">
          <span className="pane-title-text">Sites</span>
        </div>
        <div className="pane-tabs">
          {(['all', 'assigned', 'unassigned'] as FilterTab[]).map(f => (
            <Chip
              key={f}
              isClickable
              isSelected={filter === f}
              onClick={() => setFilter(f)}
              size="s"
            >
              {f === 'all' ? 'All' : f === 'assigned' ? 'Mapped' : `Not mapped (${unassignedCount})`}
            </Chip>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="pane-search">
        <TextField
          placeholder="Search sites…"
          value={searchQuery}
          onChange={setSearchQuery}
          icon={mdiMagnify}
          clearButtonProps={{ label: 'Clear' }}
        />
      </div>

      {/* Table */}
      <div className="sites-table">
        <div className="sites-thead">
          <div className="tree-th" />
          <div className="tree-th">
            <input
              ref={headerCheckRef}
              type="checkbox"
              onChange={toggleAll}
              aria-label="Select all"
            />
          </div>
          <div className="tree-th">Name</div>
          <div className="tree-th">Org unit / Location</div>
          <div className="tree-th">Status</div>

          <div className="tree-th" />
        </div>

        {filteredSites.length > 0 ? (
          filteredSites.map(site => (
            <SiteRow
              key={site.id}
              site={site}
              nodes={nodes}
              allSites={sites}
              isDragging={draggingId === site.id}
              isDragOver={false}
              isSelected={selectedIds.has(site.id)}
              onToggle={() => setSelectedIds(prev => {
                const next = new Set(prev);
                next.has(site.id) ? next.delete(site.id) : next.add(site.id);
                return next;
              })}
              onDragStart={e => onDragStart(e, 'site', site.id)}
              onAssign={onAssign}
              onUnassign={onUnassign}
              onCreate={onCreateAndAssign ? (name, type, parentId) => onCreateAndAssign(site.id, name, type, parentId) : undefined}
              onViewSiteGroups={onViewSiteGroups}
            />
          ))
        ) : (
          <div className="empty-unassigned">
            {searchQuery
              ? `No results for "${searchQuery}"`
              : filter === 'assigned'
                ? 'No sites mapped yet.'
                : filter === 'unassigned'
                  ? 'All sites are mapped.'
                  : 'No sites found.'}
          </div>
        )}
      </div>

      {filter === 'unassigned' && unassignedCount > 0 && (
        <div className="sites-pane-hint">
          Drag sites onto an Org unit or Location in the left pane to map them.
        </div>
      )}

      {selectedIds.size > 0 && (
        <div ref={bulkBarRef} className="pg-bulk-bar">
          <span className="pg-bulk-bar__count">{selectedIds.size}</span>
          <span className="pg-bulk-bar__label">site{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <div className="pg-bulk-bar__sep" />
          <div ref={bulkMapRef}>
            <button
              ref={bulkMapBtnRef}
              className="pg-bulk-bar__action"
              onClick={() => {
                if (!bulkPickerOpen && paneRef.current) {
                  const r = paneRef.current.getBoundingClientRect();
                  setBulkPickerPos({
                    bottom: 80,
                    left: r.left + r.width / 2,
                  });
                }
                setBulkPickerOpen(v => !v);
              }}
            >
              <MdiIcon path={mdiSitemap} size={0.75} />
              Map to…
            </button>
          </div>
          {bulkPickerOpen && bulkPickerPos && ReactDOM.createPortal(
            <div
              ref={bulkPickerPortalRef}
              className="bulk-picker-portal"
              style={{ position: 'fixed', bottom: bulkPickerPos.bottom, left: bulkPickerPos.left, transform: 'translateX(-50%)', zIndex: 1000 }}
            >
              <OUPickerDropdown
                nodes={nodes.filter(n => n.id !== '__global__')}
                sites={sites}
                onSelect={handleBulkAssign}
                onCreate={onBulkCreateAndAssign ? handleBulkCreate : undefined}
              />
            </div>,
            document.body
          )}
          <button
            className="pg-bulk-bar__dismiss"
            onClick={() => setSelectedIds(new Set())}
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default SitesPane;
